import { addMilliseconds, differenceInMilliseconds, isAfter, isSameDay } from 'date-fns'
import { WorkPeriod, WorkPeriodWithSummary } from '../api/work-periods'
import { Mission, MissionScheduleWeekPattern } from '../api/missions'
import { buildWorkPeriodEventLabel } from './work-period-events'
import { sortBy, get } from 'lodash'
import { BreakRule } from '../../../backend/src/services/resources/clocking-rules/clocking-rule.model'
import { WorkPeriodEvent } from '../api/work-period-events'
import { getDocIdAsString } from './documents'
import { oneHour } from './date'

/**
 * Check whether a work period is virtual (not yet persisted in DB).
 * Virtual WPs are generated by the backend for template-based missions
 * and carry an _id like "virtual-1708041600000".
 */
export const isVirtualWorkPeriod = (workPeriod: Pick<WorkPeriod, '_id'>): boolean =>
  typeof workPeriod._id === 'string' && workPeriod._id.startsWith('virtual-')

export const getWorkPeriodFromDayDate = (
  workPeriods: WorkPeriod[],
  date: Date,
): WorkPeriod | WorkPeriodWithSummary | undefined =>
  workPeriods.find(workPeriod => isSameDay(new Date(workPeriod.start.date), date))

//TODO: temp break calculation (need to be calculated by a shared util or by backend)
export const calculateWorkPeriodDuration = (
  start?: Date,
  end?: Date,
  breakRule?: BreakRule,
): number => {
  if (!start || !end || !breakRule) return 0
  const oneHour = 1000 * 60 * 60
  const breakDuration = (oneHour * breakRule.breakDuration) / 60
  const breakGoalDuration = (oneHour * breakRule.breakAllowedAfter) / 60
  let diff = differenceInMilliseconds(new Date(end), new Date(start))
  if (diff >= breakGoalDuration) diff -= breakDuration
  return diff
}
export const calculateWorkPeriodsDuration = (
  workPeriods: { start?: { date: Date }; end?: { date: Date } }[],
  breakRule?: BreakRule,
): number => {
  const totalDuration = workPeriods.reduce((duration, workPeriod) => {
    if (workPeriod.start && workPeriod.end) {
      return (
        duration +
        calculateWorkPeriodDuration(
          new Date(workPeriod.start.date),
          new Date(workPeriod.end.date),
          breakRule,
        )
      )
    } else return duration
  }, 0)
  return totalDuration
}

/**
 * Duration in ms for a single day slot (startHour/endHour in 0–24).
 * Handles overnight slots (endHour < startHour). Does not apply break.
 */
const slotDurationMs = (startHour: number, endHour: number): number => {
  const hours = endHour >= startHour ? endHour - startHour : 24 - startHour + endHour
  return hours * oneHour
}

/**
 * Total duration in ms for one week pattern, with break rule applied per slot.
 * Same logic as calculateWorkPeriodDuration: if slot duration >= breakAllowedAfter, subtract breakDuration.
 */
export const calculateScheduleTemplateWeekDuration = (
  weekPattern: MissionScheduleWeekPattern,
  breakRule?: BreakRule,
): number => {
  const breakDurationMs = breakRule ? (oneHour * breakRule.breakDuration) / 60 : 0
  const breakGoalMs = breakRule ? (oneHour * breakRule.breakAllowedAfter) / 60 : 0

  let total = 0
  const days = weekPattern.days ? Object.entries(weekPattern.days) : []
  for (const [, slot] of days) {
    if (!slot || typeof slot.startHour !== 'number' || typeof slot.endHour !== 'number') continue
    let diff = slotDurationMs(slot.startHour, slot.endHour)
    if (breakRule && diff >= breakGoalMs) diff -= breakDurationMs
    total += diff
  }
  return total
}

export const isAllWeekWorkPeriodsPreBillied = (
  mission: Mission,
  week: number,
  year: number,
): string | undefined => {
  const weekWorkPeriods = mission.workPeriods.filter(w => w.year === year && w.week === week)
  return weekWorkPeriods[0]?.periodSummary
}

const buildEventLabel = (event: WorkPeriod['events'][0]): string =>
  buildWorkPeriodEventLabel(event.item)

export const buildEventsLabels = (events: WorkPeriod['events']): string =>
  sortBy(events, 'item.code').map(buildEventLabel).join('\n')

export const getWorkPeriodsWithEvent = (workPeriods: WorkPeriod[], eventId: string): WorkPeriod[] =>
  workPeriods.filter(w => w.events.some(e => e.item._id === eventId))

export const hasBenefitEvent = (workPeriod: WorkPeriod): boolean =>
  workPeriod.events.some(e => e.item.type === 'benefit')

export const getEventsByType = (
  workPeriod: WorkPeriod,
  eventType: WorkPeriodEvent['type'],
): WorkPeriod['events'] => workPeriod.events.filter(e => e.item.type === eventType)

/**
 * Type representing possible colors for work period elements
 */
export type WorkPeriodColor = 'danger' | 'success' | 'warning' | 'info' | 'dark' | undefined

/**
 * Determines the color for a work period element based on its state and type
 * @param workPeriod The work period to analyze
 * @param elementType The type of element to get color for (e.g. 'start', 'end', 'break')
 * @param elementState Optional state of the element (e.g. 'counted', 'clocked', 'manual')
 * @returns The appropriate color for the element
 */
export const getWorkPeriodElementColor = (
  workPeriod: WorkPeriodWithSummary,
  elementType: 'start' | 'end' | 'break' | 'event' | 'other',
): WorkPeriodColor => {
  // Benefit events are always blue
  if (hasBenefitEvent(workPeriod) && elementType === 'event') {
    return 'info'
  }

  // For breaks: red if manually overridden, orange if differs from theoretical
  if (elementType === 'break') {
    if (workPeriod.manualBreak) {
      return 'danger'
    }
    if (
      workPeriod.summary.durations.breaks &&
      workPeriod.summary.durations.breaks !== workPeriod.summary.durations.theoreticalBreaks
    ) {
      return 'warning'
    }
  }

  // For start/end times
  if (elementType === 'start' || elementType === 'end') {
    const time = elementType === 'start' ? workPeriod.start : workPeriod.end
    const counted = get(workPeriod.summary, `${elementType}.counted`)
    const clockingRule = workPeriod.clockingRules.rules[elementType]

    // Manual correction is red
    if (counted === 'manual') {
      return 'danger'
    }

    // Clocking with theoretical time is green
    if (time.clocking && counted === 'theoretical') {
      return 'success'
    }

    // Clocking with clocked time is yellow
    if (time.clocking && counted === 'clocked') {
      return 'warning'
    }

    /**
     * Late arrivals or departures, outpassing the clocking rule are yellow
     * Only for not validated work periods
     */
    if (!workPeriod.isValidated) {
      if (clockingRule.late.mode === 'theoritical') {
        // The clocking rule has no tolerance, warning is shown after theoritical time is passed
        if (isAfter(new Date(), new Date(time.date))) {
          return 'warning'
        }
      } else {
        // The clocking rule has tolerance, warning is shown taking tolerance into account
        if (
          isAfter(new Date(), addMilliseconds(new Date(time.date), clockingRule.late.tolerance))
        ) {
          return 'warning'
        }
      }
    }
  }

  return undefined
}

/**
 * Interface for work period cell data
 */
export interface WorkPeriodCellData {
  color: WorkPeriodColor
  tooltipId: string
  date: Date | null
}

/**
 * Gets all necessary data for rendering a work period cell
 * @param workPeriod The work period to get cell data for
 * @param type The type of cell ('start', 'end', 'break')
 * @returns Object containing color, tooltipId and date for the cell
 */
export const getWorkPeriodCellData = (
  workPeriod: WorkPeriodWithSummary,
  type: 'start' | 'end' | 'break',
): WorkPeriodCellData => {
  const color = getWorkPeriodElementColor(workPeriod, type)
  const tooltipId = `${getDocIdAsString(workPeriod.mission)}:${getDocIdAsString(
    workPeriod,
  )}:${type}`
  let date: Date | null = null

  if (type !== 'break') {
    const counted = workPeriod.summary[type].counted
    date = counted ? workPeriod.summary[type][counted] : null
  }

  return {
    color,
    tooltipId,
    date,
  }
}
